/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  CellType,
  ComponentType,
  IMediaModel,
  Orientation,
  ThemeContext,
  channelTagsFromMedia,
  useGetMedia,
} from "@nf/common";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useContext, useEffect } from "react";

import { GridComponent } from "components/Configuration";
import SortingDropdown from "components/Dropdown/SortingDropdown";
import MediaArticleField from "components/Media/MediaArticleField";
import MediaDescription from "components/Media/MediaDescription";
import { buildSortField } from "helpers";
import { useLanguage } from "hooks";
import MediaDetailsScreen, {
  IMediaDetailsParams,
  useMediaDetailsActionsSelector,
  useMediaDetailsMediaSelector,
} from "../MediaDetailsScreen";
import { filterOptions } from "./const";

import styles from "./CollectionScreen.module.scss";

const MediaDetailsError = dynamic(
  () => import("components/Media/MediaDetailsErrorModal")
);
export interface CollectionScreenProps {
  media?: IMediaModel;
}

const CollectionScreen = ({ media: mediaProps }: CollectionScreenProps) => {
  const { t } = useTranslation("translations");
  const channelContext = useContext(ThemeContext);
  const language = useLanguage();
  const router = useRouter();
  const params = router.query as IMediaDetailsParams;

  const { isLoading, error } = useMediaDetailsActionsSelector();
  const mediaState = useMediaDetailsMediaSelector();
  const media = mediaState || mediaProps;

  const getMedia = useGetMedia();
  const id = params.id;
  const sort = params.sort;

  useEffect(() => {
    if (id) {
      getMedia({
        MediaId: id,
        QueryParams: {
          sort: buildSortField(sort, language),
        },
        ...filterOptions,
      });
    }
  }, [id, sort]);

  useEffect(() => {
    if (media && channelContext.onBrandingChange) {
      channelContext.onBrandingChange(channelTagsFromMedia(media));
    }
    return () => {
      // Restoring default branding
      channelContext?.onBrandingChange?.();
    };
  }, [media]);

  if (!isLoading && error) {
    return <MediaDetailsError />;
  }

  if (!media) {
    return null;
  }

  return (
    <MediaDetailsScreen media={media} className={styles.background}>
      <div className={styles.container}>
        <h1 className={styles.title}>{media.Title}</h1>

        <div className={styles.metadata}>
          <h3 className={styles.subtitle}>{t("web_collection")} </h3>
          <span className={styles.count}>{media.Media?.length} films</span>
        </div>

        <div className={styles.content}>
          <MediaDescription media={media} className={styles.description} />

          <div className={styles.sort}>
            <SortingDropdown
              navigateOptions={{ shallow: true, scroll: false }}
            />
          </div>
        </div>

        <GridComponent
          className={styles.grid}
          component={{
            CellType: CellType.Frame,
            ComponentTypeCode: ComponentType.List,
            Orientation: Orientation.Grid,
            MediaList: media?.Media,
            QueryParams: {
              collection: media?.Id,
            },
          }}
        />

        <MediaArticleField article={media.Article} />
      </div>
    </MediaDetailsScreen>
  );
};

export default CollectionScreen;
